import React from 'react';
import { Question, Questionnaire } from 'models/Questionnaire';
import { PreliminaryData, AnswerData } from 'models/ResultPacket/ResultPacket';
import { PreliminaryValue } from 'models/ResultPacket/Preliminary';
import TermsModal from 'components/modals/TermsModal';
import { Redirect } from 'react-router';
import { ResetModal } from 'components/modals/ResetModal';
import getTimestamp from 'Utilities/timestamp';

export interface QuestionnaireState {
	token: string | null;
	answers: {
		[id: string]: AnswerData;
	};
	lastCategory: string | null;
	lastState?: string;
	questionnaire: Questionnaire | null;
	preliminary: PreliminaryData;
	canJumpToSummary: boolean;
	termsAccepted: boolean | null;
	timestamp: number | null;


}

export interface QuestionnaireSession extends QuestionnaireState {
	setPrimaryAnswer: (question: Question, value: number) => void;
	setSecondaryAnswer: (question: Question, value: number) => void;
	setLastCategory: (categoryId: string) => void;
	setPreliminary: (property: keyof PreliminaryData, value: PreliminaryValue | string) => void;
	setLastState: (property: string) => void;
	startQuestionnaire: (questionnaire: Questionnaire, token: string) => void;
	setInprogress: (value: boolean) => void;
	setTermsAccepted: (value: boolean) => void;
	setCanJumpToSummary: (value: boolean) => void;
	finish: () => void;
	clear: () => void;
	setHighlight: (question: Question, value: boolean) => void;
	getHighlightCount: () => number;
	inprogress: boolean;
	preliminaryFilled: boolean;
	setHeaderElement: (element: any) => void;
	headerElement: any;

}

const initialQuestionnaireSession: QuestionnaireSession = {
	setPrimaryAnswer: (question: Question, value: number) => { },
	setSecondaryAnswer: (question: Question, value: number) => { },
	setPreliminary: (property: keyof PreliminaryData, value: PreliminaryValue | string) => { },
	setLastState: (property: string) => { },
	startQuestionnaire: (questionnaire: Questionnaire, token: string) => { },
	setInprogress: (value: boolean) => { },
	setTermsAccepted: (value: boolean) => { },
	setCanJumpToSummary: (value: boolean) => { },
	finish: () => { },
	setLastCategory: (categoryId: string) => { },
	setHighlight: (question: Question, value: boolean) => { },
	getHighlightCount: (): number => { return 0; },
	answers: {},
	questionnaire: null,
	preliminary: {},
	canJumpToSummary: false,
	clear: () => { },
	token: null,
	inprogress: false,
	termsAccepted: null,
	preliminaryFilled: false,
	lastCategory: null,
	timestamp: null,
	setHeaderElement: (element: any) => { },
	headerElement: undefined

}

const initialQuestionnaireState: QuestionnaireState = {
	token: null,
	canJumpToSummary: false,
	lastState: undefined,
	answers: {},
	preliminary: {},
	questionnaire: null,
	termsAccepted: null,
	lastCategory: null,
	timestamp: null
}

const QuestionnaireContext = React.createContext<QuestionnaireSession>(initialQuestionnaireSession);
export const QuestionnaireConsumer = QuestionnaireContext.Consumer;
export default QuestionnaireContext;

const storageKey = 'kuntoutussäätiö.questionnaire';

const sessionExpirationDuration = 60 /* Seconds */
	* 60 /* Minutes */
	* 7 /* Hours*/;

const highlightMaxCount = 3;

export class QuestionnaireProvider extends React.Component<{}, QuestionnaireSession> {

	static instance: QuestionnaireProvider;

	checkPreliminaries(preliminaries: PreliminaryData): boolean {
		let valid = true;
		valid = (preliminaries.activity !== undefined
			|| preliminaries.education !== undefined
			|| preliminaries.fillreason !== undefined
			|| preliminaries.firstlanguage !== undefined
			|| preliminaries.gender !== undefined);

		if (valid && preliminaries.age) {
			const age = parseInt(preliminaries.age);
			if (age <= 0 || age > 125) {
				valid = false;
			}
		}

		if (valid) {
			const prels = Object.entries(preliminaries).filter(x => x[0] !== 'age');
			valid = prels.length === 5;

			if (valid) {
				prels.forEach(x => {
					if (x[0] !== 'age' && x[0] !== 'gender') {
						const value = x[1] as PreliminaryValue;
						if (value.value === 'other' && (value.other == null || value.other.length === 0)) {
							valid = false;
							return;
						}
					}
				});
			}
		}

		return valid;
	}

	setPreliminary(property: keyof PreliminaryData, value: PreliminaryValue | string) {
		if (typeof value === 'string' && property !== 'age') {
			return;
		}

		this.setState((oldState) => {
			let preliminaries = {
				...oldState.preliminary,
				[property]: value
			};

			const preliminariesValid = this.checkPreliminaries(preliminaries);
			return {
				...oldState,
				preliminary: {
					...oldState.preliminary,
					[property]: value
				},
				preliminaryFilled: preliminariesValid
			} as QuestionnaireSession;
		});
	}
	setLastState(property: string) {
		this.setState((oldState) => {
			const summaryJump = oldState.canJumpToSummary === false && property === 'summary' ? true : oldState.canJumpToSummary;
			return {
				...oldState,
				canJumpToSummary: summaryJump,
				lastState: property
			};
		});
	}
	startQuestionnaire(questionnaire: Questionnaire, token: string) {
		this.setState({
			token: token,
			inprogress: true,
			questionnaire: questionnaire,
			timestamp: getTimestamp(),
			answers: {}
		});
	}

	setPrimaryAnswer(question: Question, value: number) {
		this.setState((oldState) => {
			let answer = { ...oldState.answers[question.id] };
			if (answer?.id === undefined) {
				answer = { id: question.id };
			}
			answer.primary = value;
			let answers = {
				...oldState.answers,
				[question.id]: answer
			};

			return { ...oldState, answers };
		});
	}

	setSecondaryAnswer(question: Question, value: number) {
		this.setState((oldState) => {
			let answer = { ...oldState.answers[question.id] };
			if (answer?.id === undefined) {
				answer = { id: question.id };
			}
			answer.secondary = value;
			const answers = {
				...oldState.answers,
				[question.id]: answer
			};

			return { ...oldState, answers };
		});
	}

	setInprogress(value: boolean) {
		this.setState({ inprogress: value });
	}

	clear() {
		clearLocalSession();
	}

	setTermsAccepted(value: boolean) {
		this.setState({ termsAccepted: value });
	}

	setHighlight(question: Question, value: boolean) {
		if (value === true && this.getHighlightCount() >= highlightMaxCount)
			return;
		this.setState((oldState) => {
			let answer = { ...oldState.answers[question.id] };
			if (answer?.id === undefined) {
				answer = { id: question.id };
			}
			answer.highlighted = value;
			const answers = {
				...oldState.answers,
				[question.id]: answer
			};

			return { ...oldState, answers };
		});
	}

	getHighlightCount(): number {
		const answerList = Object.values(this.state.answers);
		let highlightCount = 0;
		answerList.forEach(answer => {
			if (answer.highlighted === true)
				highlightCount++;
		});
		return highlightCount;
	}

	componentDidUpdate() {
		if (this.state.questionnaire && this.state.token) {
			if (!this.state.timestamp || this.state.timestamp < getTimestamp(-sessionExpirationDuration)) {
				this.clear();
				storeLocalSession(initialQuestionnaireState);
				return;
			}
			storeLocalSession(this.state as QuestionnaireState);
		} else {
			this.clear();
		}
	}

	reset() {
		this.setState({ questionnaire: null, token: null });
	}

	end() {
		this.clear();
		this.redirectToHome = true;
		this.setState({ questionnaire: null, token: null });
	}

	private redirectToLastState: boolean = true;

	private redirectToHome: boolean = false;

	constructor(props: {}) {
		super(props);

		QuestionnaireProvider.instance = this;
		let QuestionnaireState = readLocalSession();
		if (QuestionnaireState.timestamp && QuestionnaireState.timestamp < getTimestamp(-sessionExpirationDuration /* two hours */)) {
			clearLocalSession();
			QuestionnaireState = initialQuestionnaireState;
		}
		
		this.setPrimaryAnswer = this.setPrimaryAnswer.bind(this);
		this.setSecondaryAnswer = this.setSecondaryAnswer.bind(this);
		this.setLastState = this.setLastState.bind(this);
		this.setPreliminary = this.setPreliminary.bind(this);
		this.startQuestionnaire = this.startQuestionnaire.bind(this);
		this.setInprogress = this.setInprogress.bind(this);
		this.clear = this.clear.bind(this);
		this.setTermsAccepted = this.setTermsAccepted.bind(this);
		this.setHighlight = this.setHighlight.bind(this);
		this.getHighlightCount = this.getHighlightCount.bind(this);

		this.state = {
			...QuestionnaireState,
			setPrimaryAnswer: this.setPrimaryAnswer,
			setSecondaryAnswer: this.setSecondaryAnswer,
			setPreliminary: this.setPreliminary,
			setLastState: this.setLastState,
			startQuestionnaire: this.startQuestionnaire,
			setInprogress: this.setInprogress,
			setTermsAccepted: this.setTermsAccepted,
			clear: this.clear,
			inprogress: false,
			preliminaryFilled: this.checkPreliminaries(QuestionnaireState.preliminary),
			setCanJumpToSummary: (value: boolean): void => { this.setState({ canJumpToSummary: value }) },
			finish: () => { this.setState({ questionnaire: null, token: null, inprogress: false }) },
			setLastCategory: (categoryId) => { this.setState({ lastCategory: categoryId }) },
			setHeaderElement: (element) => { this.setState({ headerElement: element }) },
			setHighlight: this.setHighlight,
			getHighlightCount: this.getHighlightCount,
			headerElement: undefined
		}

	}

	static getState() {
		return QuestionnaireProvider.instance.state;
	}

	termsDeclined() {
		clearLocalSession();
		this.setState({ termsAccepted: false })
	}

	render() {
		if (this.redirectToHome) {
			this.redirectToHome = false;
			return <Redirect to={'/'}/>
		}

		if (this.redirectToLastState) {
			this.redirectToLastState = false;
			if(this.state.lastState && this.state.lastState !== window.location.pathname + window.location.search)
				return <Redirect to={this.state.lastState} />
		}
		
		return (
			<QuestionnaireContext.Provider value={this.state}>
				{
					(this.state.inprogress === false &&
						this.state.token !== null &&
						this.state.questionnaire !== null) &&
					<ResetModal onContinue={() => this.setState({ inprogress: true })}
						onCancel={() => { this.end(); }}
						questionnaireTitle={this.state.questionnaire.name}
						show={true} />
				}
				{
					this.state.termsAccepted === false &&
					<Redirect to='/' />
				}
				{
					window.location.pathname.endsWith("result") === false &&
					<TermsModal show={this.state.termsAccepted === null}
						onHide={() => this.termsDeclined()}
						Accepted={() => this.setState({ termsAccepted: true })} />
				}

				{this.props.children}
			</QuestionnaireContext.Provider>
		)
	}
}

function readLocalSession(): QuestionnaireState {
	const data = localStorage.getItem(storageKey);
	if (data == null) {
		return initialQuestionnaireSession;
	}
	return JSON.parse(data);
}

function storeLocalSession(context: QuestionnaireState) {
	localStorage.setItem(storageKey, JSON.stringify(context))
}

function clearLocalSession() {
	localStorage.removeItem(storageKey);
}